import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Activity,
  ChevronDown,
  ChevronUp,
  Terminal,
  CheckCircle2,
  AlertCircle,
  Clock,
  Copy,
  Check,
  RefreshCw,
  Sliders,
  FileText,
  Bot,
  Sparkles,
  Layers,
  Database,
  Tag,
  Hash,
  FileCode,
  CheckSquare,
} from 'lucide-react';
import api from '../services/api';
import { SignalLogItem, IngestionEventItem } from '../types';

interface SignalTraceViewerProps {
  signalId: string;
  signalExternalId?: string;
}

type FilterTab = 'all' | 'pipeline' | 'audit';

const STANDARD_STAGES = [
  { key: 'scraped', label: '1. Scraped', desc: 'Fetched from upstream' },
  { key: 'deduped_new', alias: 'deduped', label: '2. Deduped', desc: 'Verified unique' },
  { key: 'structured', label: '3. Structured', desc: 'Schema normalized' },
  { key: 'classified', label: '4. Classified', desc: 'AI regulatory triage' },
];

export const SignalTraceViewer: React.FC<SignalTraceViewerProps> = ({ signalId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [logs, setLogs] = useState<SignalLogItem[]>([]);
  const [ingestionEvents, setIngestionEvents] = useState<IngestionEventItem[]>([]);
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [expandedLogIds, setExpandedLogIds] = useState<Record<string, boolean>>({});
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const fetchLogs = useCallback(async () => {
    if (!signalId) return;
    try {
      setLoading(true);
      setError(null);
      const res = await api.signals.getLogs(signalId);
      setLogs(res.logs || []);
      setIngestionEvents(res.ingestionEvents || []);
    } catch (err: unknown) {
      console.warn('Could not load signal logs:', err);
      setError((err as Error).message || 'Failed to load trace logs');
    } finally {
      setLoading(false);
    }
  }, [signalId]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const toggleExpandLog = (id: string) => {
    setExpandedLogIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const filteredLogs = useMemo(() => {
    if (activeTab === 'pipeline') {
      return logs.filter((l) => l.sourceType === 'pipeline_event');
    }
    if (activeTab === 'audit') {
      return logs.filter((l) => l.sourceType === 'audit_log');
    }
    return logs;
  }, [logs, activeTab]);

  const pipelineCount = useMemo(
    () => logs.filter((l) => l.sourceType === 'pipeline_event').length,
    [logs]
  );
  const auditCount = useMemo(() => logs.filter((l) => l.sourceType === 'audit_log').length, [logs]);

  const completedStages = useMemo(() => {
    const stageTypes = new Set(ingestionEvents.map((e) => e.event_type.toLowerCase()));
    return stageTypes;
  }, [ingestionEvents]);

  const latestLog = logs[logs.length - 1];

  const getCategoryIcon = (category: SignalLogItem['category'], stage: string) => {
    if (category === 'triage') return <Sliders className="w-3.5 h-3.5 text-blue-600" />;
    if (category === 'notes') return <FileText className="w-3.5 h-3.5 text-emerald-600" />;
    if (category === 'ai_query') return <Bot className="w-3.5 h-3.5 text-purple-600" />;
    if (stage === 'classified') return <Sparkles className="w-3.5 h-3.5 text-indigo-600" />;
    if (stage === 'scraped' || stage === 'structured')
      return <Layers className="w-3.5 h-3.5 text-sky-600" />;
    return <Activity className="w-3.5 h-3.5 text-slate-600" />;
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200/90 shadow-[0_1px_3px_rgba(15,23,42,0.03)] overflow-hidden transition-all duration-200">
      {/* Collapsed/Header Bar */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-5 py-3.5 flex items-center justify-between bg-slate-50/70 hover:bg-slate-100/70 transition cursor-pointer text-left focus:outline-none"
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-7 h-7 rounded-lg bg-slate-200/80 flex items-center justify-center text-slate-700 shrink-0">
            <Terminal className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-xs text-slate-800 tracking-tight">
                Signal Traces & Execution Logs
              </span>
              <span className="bg-slate-200/90 text-slate-700 text-[10px] font-mono font-medium px-2 py-0.5 rounded-full">
                {logs.length} {logs.length === 1 ? 'event' : 'events'}
              </span>
              {latestLog && (
                <span className="hidden sm:inline-flex items-center gap-1 text-[11px] text-slate-500 font-mono">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                  Last: <span className="text-slate-700 font-medium">{latestLog.stage}</span>
                </span>
              )}
            </div>
            <p className="text-[11px] text-slate-500 truncate mt-0.5">
              Pipeline journey, model inferences, step outputs, and trace identifiers
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0 ml-3">
          <span className="text-[11px] text-slate-500 font-medium hidden md:inline">
            {isOpen ? 'Collapse' : 'Expand Trace Viewer'}
          </span>
          <div className="w-6 h-6 rounded-md bg-white border border-slate-200 flex items-center justify-center text-slate-500 shadow-2xs">
            {isOpen ? (
              <ChevronUp className="w-3.5 h-3.5" />
            ) : (
              <ChevronDown className="w-3.5 h-3.5" />
            )}
          </div>
        </div>
      </button>

      {/* Expanded Content Area */}
      {isOpen && (
        <div className="p-5 border-t border-slate-200 space-y-4 bg-white animate-fade-in">
          {/* Header Controls & Refresh */}
          <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-slate-100">
            <div className="flex items-center gap-1.5 bg-slate-100 p-1 rounded-lg">
              <button
                onClick={() => setActiveTab('all')}
                className={`text-xs font-medium px-3 py-1 rounded-md transition cursor-pointer ${
                  activeTab === 'all'
                    ? 'bg-white text-slate-900 shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                All Logs ({logs.length})
              </button>
              <button
                onClick={() => setActiveTab('pipeline')}
                className={`text-xs font-medium px-3 py-1 rounded-md transition cursor-pointer ${
                  activeTab === 'pipeline'
                    ? 'bg-white text-slate-900 shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Pipeline Traces ({pipelineCount})
              </button>
              <button
                onClick={() => setActiveTab('audit')}
                className={`text-xs font-medium px-3 py-1 rounded-md transition cursor-pointer ${
                  activeTab === 'audit'
                    ? 'bg-white text-slate-900 shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Audit Trail ({auditCount})
              </button>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={fetchLogs}
                disabled={loading}
                className="text-xs text-slate-600 hover:text-slate-900 bg-slate-50 hover:bg-slate-100 border border-slate-200 px-2.5 py-1.5 rounded-lg flex items-center gap-1.5 transition cursor-pointer disabled:opacity-50"
                title="Refresh signal logs"
              >
                <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>
            </div>
          </div>

          {/* Ingestion Pipeline Stepper */}
          <div className="bg-slate-50/80 rounded-xl p-3.5 border border-slate-200/80">
            <div className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <Layers className="w-3.5 h-3.5 text-blue-600" />
              <span>Ingestion Pipeline Flow</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {STANDARD_STAGES.map((st) => {
                const isPassed =
                  completedStages.has(st.key) ||
                  (st.alias && completedStages.has(st.alias)) ||
                  (st.key === 'deduped_new' && completedStages.has('deduped_skip'));
                return (
                  <div
                    key={st.key}
                    className={`p-2.5 rounded-lg border text-xs flex flex-col justify-between transition ${
                      isPassed
                        ? 'bg-white border-emerald-200/90 text-slate-900 shadow-2xs'
                        : 'bg-slate-100/60 border-slate-200 text-slate-400'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-[11px]">{st.label}</span>
                      {isPassed ? (
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                      ) : (
                        <Clock className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      )}
                    </div>
                    <span className="text-[10px] text-slate-500 truncate">{st.desc}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Log Entries Timeline */}
          {loading && logs.length === 0 ? (
            <div className="py-8 text-center text-xs text-slate-500 flex items-center justify-center gap-2">
              <RefreshCw className="w-4 h-4 animate-spin text-blue-600" />
              <span>Fetching signal execution trace logs...</span>
            </div>
          ) : error && logs.length === 0 ? (
            <div className="py-4 px-4 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="py-6 px-4 bg-slate-50 border border-slate-200 rounded-lg text-center text-xs text-slate-500 space-y-1">
              <Database className="w-5 h-5 mx-auto text-slate-400 mb-1" />
              <p className="font-medium text-slate-700">No logs matching this category filter</p>
              <p className="text-[11px]">
                {activeTab === 'all'
                  ? 'This signal was registered in the database baseline with initial classification payload.'
                  : 'Try selecting "All Logs" to see other events.'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredLogs.map((log) => {
                const isExpanded = !!expandedLogIds[log.id];
                const d = (log.details || {}) as Record<string, unknown>;
                const hasDetails = Object.keys(d).length > 0;
                const formattedJson = hasDetails ? JSON.stringify(d, null, 2) : '';

                return (
                  <div
                    key={log.id}
                    className="border border-slate-200 rounded-xl bg-white shadow-2xs overflow-hidden text-xs transition"
                  >
                    {/* Entry Header Bar */}
                    <div className="p-3.5 bg-slate-50/70 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
                      <div className="flex items-start sm:items-center gap-2.5 min-w-0">
                        <div className="p-1.5 bg-white rounded-md border border-slate-200/80 shadow-2xs shrink-0 mt-0.5 sm:mt-0">
                          {getCategoryIcon(log.category, log.stage)}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono font-bold text-slate-900 text-xs">
                              {log.stage}
                            </span>
                            <span className="bg-slate-200 text-slate-700 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider">
                              {log.sourceType === 'pipeline_event' ? 'Pipeline' : 'Audit'}
                            </span>
                            {log.status === 'error' && (
                              <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded text-[10px] font-bold">
                                Failed
                              </span>
                            )}
                            <span className="text-[11px] text-slate-500 font-sans truncate">
                              by <span className="font-medium text-slate-700">{log.actorName}</span>
                            </span>
                          </div>
                          <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                            {new Date(log.timestamp).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit',
                              timeZoneName: 'short',
                            })}
                          </div>
                        </div>
                      </div>

                      {/* Right metadata and buttons */}
                      <div className="flex items-center gap-2 self-end sm:self-auto shrink-0 flex-wrap">
                        {log.traceId && (
                          <button
                            onClick={() => handleCopy(log.traceId!, `tr-${log.id}`)}
                            className="text-[10px] font-mono bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 px-2 py-1 rounded flex items-center gap-1 transition cursor-pointer"
                            title={`Langfuse Trace ID: ${log.traceId}`}
                          >
                            {copiedId === `tr-${log.id}` ? (
                              <Check className="w-3 h-3 text-emerald-600" />
                            ) : (
                              <Copy className="w-3 h-3 text-blue-600" />
                            )}
                            <span>Trace: {log.traceId.substring(0, 8)}...</span>
                          </button>
                        )}

                        {log.runId && (
                          <button
                            onClick={() => handleCopy(log.runId!, `run-${log.id}`)}
                            className="text-[10px] font-mono bg-slate-100 text-slate-600 hover:bg-slate-200 px-2 py-1 rounded flex items-center gap-1 transition cursor-pointer"
                            title={`Ingestion Run ID: ${log.runId}`}
                          >
                            {copiedId === `run-${log.id}` ? (
                              <Check className="w-3 h-3 text-emerald-600" />
                            ) : (
                              <Copy className="w-3 h-3 text-slate-500" />
                            )}
                            <span>Run: {log.runId.substring(0, 8)}...</span>
                          </button>
                        )}

                        {hasDetails && (
                          <button
                            onClick={() => toggleExpandLog(log.id)}
                            className="text-[11px] font-medium text-slate-700 hover:text-slate-900 bg-white border border-slate-300 hover:bg-slate-50 px-2.5 py-1 rounded-lg flex items-center gap-1 transition cursor-pointer shadow-2xs"
                          >
                            <span>{isExpanded ? 'Hide Payload' : 'View Payload'}</span>
                            {isExpanded ? (
                              <ChevronUp className="w-3 h-3" />
                            ) : (
                              <ChevronDown className="w-3 h-3" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Step Output / Decision Highlights */}
                    <div className="p-3.5 bg-white space-y-2.5">
                      {/* Classified Stage Output */}
                      {log.stage === 'classified' && (
                        <div className="space-y-2">
                          <div className="flex flex-wrap gap-2 text-[11px]">
                            {typeof d.product_line === 'string' && (
                              <span className="bg-slate-100 border border-slate-200 text-slate-800 px-2.5 py-1 rounded-md font-medium flex items-center gap-1.5">
                                <Tag className="w-3 h-3 text-blue-600" />
                                Product Line:{' '}
                                <strong className="text-slate-900 capitalize">
                                  {d.product_line.replace(/_/g, ' ')}
                                </strong>
                              </span>
                            )}
                            {typeof d.urgency === 'string' && (
                              <span className="bg-slate-100 border border-slate-200 text-slate-800 px-2.5 py-1 rounded-md font-medium flex items-center gap-1.5">
                                Urgency:{' '}
                                <strong className="text-slate-900 uppercase">{d.urgency}</strong>
                              </span>
                            )}

                            {typeof d.action_items_count === 'number' && (
                              <span className="bg-amber-50 border border-amber-200 text-amber-900 px-2.5 py-1 rounded-md font-medium flex items-center gap-1.5">
                                <CheckSquare className="w-3 h-3 text-amber-700" />
                                Action Items: <strong>{d.action_items_count} generated</strong>
                              </span>
                            )}
                          </div>
                          {Array.isArray(d.impacted_products) && d.impacted_products.length > 0 && (
                            <div className="text-[11px] text-slate-600 bg-slate-50 p-2.5 rounded-lg border border-slate-200">
                              <span className="font-semibold text-slate-700">
                                Detected Devices:{' '}
                              </span>
                              <span>{d.impacted_products.join(', ')}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Structured Stage Output */}
                      {log.stage === 'structured' && (
                        <div className="space-y-2 text-[11px]">
                          {typeof d.extracted_title === 'string' && d.extracted_title && (
                            <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-200 text-slate-700">
                              <span className="font-semibold text-slate-900">
                                Extracted Title:{' '}
                              </span>
                              <span>{d.extracted_title}</span>
                            </div>
                          )}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 font-mono text-[10px]">
                            {typeof d.raw_s3_key === 'string' && (
                              <div className="bg-slate-50 p-2 rounded border border-slate-200 flex items-center justify-between gap-2 overflow-hidden">
                                <div className="truncate text-slate-600">
                                  <FileCode className="w-3 h-3 text-sky-600 inline mr-1" />
                                  <span>{d.raw_s3_key}</span>
                                </div>
                                <button
                                  onClick={() => handleCopy(d.raw_s3_key as string, `s3-${log.id}`)}
                                  className="text-slate-400 hover:text-slate-700 p-1 shrink-0 cursor-pointer"
                                  title="Copy S3 Key"
                                >
                                  {copiedId === `s3-${log.id}` ? (
                                    <Check className="w-3 h-3 text-emerald-600" />
                                  ) : (
                                    <Copy className="w-3 h-3" />
                                  )}
                                </button>
                              </div>
                            )}
                            {typeof d.signal_hash === 'string' && (
                              <div className="bg-slate-50 p-2 rounded border border-slate-200 flex items-center justify-between gap-2 overflow-hidden">
                                <div className="truncate text-slate-600">
                                  <Hash className="w-3 h-3 text-purple-600 inline mr-1" />
                                  <span>SHA256: {d.signal_hash}</span>
                                </div>
                                <button
                                  onClick={() =>
                                    handleCopy(d.signal_hash as string, `hash-${log.id}`)
                                  }
                                  className="text-slate-400 hover:text-slate-700 p-1 shrink-0 cursor-pointer"
                                  title="Copy Content Hash"
                                >
                                  {copiedId === `hash-${log.id}` ? (
                                    <Check className="w-3 h-3 text-emerald-600" />
                                  ) : (
                                    <Copy className="w-3 h-3" />
                                  )}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Scraped & Deduplication Stages */}
                      {log.stage === 'scraped' && (
                        <div className="flex flex-wrap gap-2 text-[11px]">
                          {typeof d.source === 'string' && (
                            <span className="bg-slate-100 border border-slate-200 text-slate-700 px-2 py-0.5 rounded">
                              Source: <strong>{d.source}</strong>
                            </span>
                          )}
                          {typeof d.raw_payload_bytes === 'number' && (
                            <span className="bg-slate-100 border border-slate-200 text-slate-700 px-2 py-0.5 rounded font-mono">
                              Size: <strong>{d.raw_payload_bytes.toLocaleString()} bytes</strong>
                            </span>
                          )}
                          {typeof d.signal_external_id === 'string' && (
                            <span className="bg-slate-100 border border-slate-200 text-slate-700 px-2 py-0.5 rounded font-mono">
                              Ext ID: <strong>{d.signal_external_id}</strong>
                            </span>
                          )}
                        </div>
                      )}

                      {log.stage === 'deduped_new' && (
                        <div className="flex items-center gap-2 text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 p-2 rounded-lg">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                          <span>
                            Deduplication check passed: verified new unique notice for database
                            ingestion.
                          </span>
                        </div>
                      )}

                      {/* Summary Fallback if generic */}
                      {log.stage !== 'classified' &&
                        log.stage !== 'structured' &&
                        log.stage !== 'scraped' &&
                        log.stage !== 'deduped_new' && (
                          <div className="text-[11px] text-slate-700">{log.summary}</div>
                        )}
                    </div>

                    {/* Expandable JSON details payload */}
                    {isExpanded && hasDetails && (
                      <div className="p-3 bg-slate-950 text-slate-200 border-t border-slate-200 font-mono text-[11px] overflow-x-auto relative">
                        <div className="flex justify-between items-center pb-2 mb-2 border-b border-slate-800 text-slate-400 text-[10px]">
                          <span>RAW STEP JSON PAYLOAD & CONTEXT</span>
                          <button
                            onClick={() => handleCopy(formattedJson, `json-${log.id}`)}
                            className="text-slate-300 hover:text-white flex items-center gap-1 bg-slate-800 hover:bg-slate-700 px-2 py-0.5 rounded transition cursor-pointer"
                          >
                            {copiedId === `json-${log.id}` ? (
                              <>
                                <Check className="w-3 h-3 text-emerald-400" />
                                <span>Copied</span>
                              </>
                            ) : (
                              <>
                                <Copy className="w-3 h-3" />
                                <span>Copy JSON</span>
                              </>
                            )}
                          </button>
                        </div>
                        <pre className="text-emerald-400 whitespace-pre-wrap leading-relaxed select-all">
                          {formattedJson}
                        </pre>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
